import { useTranslation } from "react-i18next";
import { ShieldCheck, Sparkles, Wallet } from "lucide-react";
import { useCheckout } from "../contexts/CheckoutContext";
import { useAuth } from "../contexts/AuthContext";
import { useWallet } from "../hooks/useWallet";

/**
 * WalletBadge — plan pill + NXT balance + Recharge shortcut.
 * Clicking Recharge opens the CheckoutModal with the default Pro-tier NXT
 * package preselected. Wired via useCheckout (global).
 */
export function WalletBadge() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { plan, balance, isPro } = useWallet();
  const { openCheckout } = useCheckout();
  const isAdmin = user?.role === "admin";

  const onRecharge = () => {
    openCheckout({ kind: "recharge", packageId: "nxt_500" });
  };

  return (
    <div
      className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] backdrop-blur-md px-2 py-1.5 text-xs"
      data-testid="wallet-badge"
    >
      <span
        className={
          "inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold uppercase tracking-wider text-[10px] " +
          (isPro
            ? "bg-gradient-to-r from-[#F4D77A] to-[#A8862B] text-[#0a0a0d]"
            : "bg-white/[0.05] text-white/70 border border-white/10")
        }
        data-testid="wallet-plan"
      >
        {isPro ? <Sparkles className="h-3 w-3" /> : null}
        {plan}
      </span>

      {isAdmin ? (
        <span
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold uppercase tracking-wider text-[10px] border border-[#D4AF37]/50 bg-[#D4AF37]/10 text-[#F4D77A]"
          data-testid="wallet-admin-badge"
          title="Admin account"
        >
          <ShieldCheck className="h-3 w-3" />
          {t("wallet.admin_badge")}
        </span>
      ) : null}

      <span
        className="inline-flex items-center gap-1 px-1 text-white/85 font-semibold tabular-nums"
        data-testid="wallet-balance"
      >
        <Wallet className="h-3.5 w-3.5 text-[#D4AF37]" />
        <span>{balance}</span>
        <span className="text-[#D4AF37]/85">NXT</span>
      </span>

      <button
        type="button"
        onClick={onRecharge}
        className="rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[11px] font-medium text-white/75 hover:text-white hover:bg-white/[0.07] hover:border-white/20 transition-colors"
        data-testid="wallet-recharge-btn"
      >
        {t("auditor.wallet.recharge")}
      </button>
    </div>
  );
}

export default WalletBadge;
